import emojiRegex from 'emoji-regex'
import { permute, powerSet } from './func'
import { TourType } from './types.gen'

export const regex = {
  email: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
  phone: /^((\+\d{1,2}\s?)|1)??\(?\d{3}\)?[\s.-]?\d{3}(?: )?[\s.-]?(?: )?\d{4}$/,
  phoneUnformatted: /^(\\+\\d{7}\\d*)|\\d{10}$/,
  isoDate: /^[1-2][0-9][0-9][0-9]-[0-1][0-9]-[0-3][0-9]$/,
  alphanumeric: /^[0-9a-z]+$/i,
  username: /^[@.a-z0-9_-]+$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$/,
  // not exclusive because they are scattered
  controlFormatChars: /[\x00-\x09\x0B-\x0C\x0E-\x1F\x7F-\x9F\u202A-\u202E]/g,
}

export interface ValidatorResp<T> {
  error?: Error
  value?: T | null
}

export interface Validator<T> {
  validate(value: string): ValidatorResp<T>
}

class BaseValidator {
  private cfgRegex: RegExp | null // regexp to validate against
  private cfgReplace: { replaceRegex: RegExp; replacement: string } | null // string replace
  private cfgEmpty: string[] // values considered empty
  private cfgValidStrings: string[] | null // list of valid strings (everything else invalid)
  private cfgCaseInsensitive: boolean // valid strings matching has case sensitivity
  private cfgPred: ((s: string) => boolean) | null
  private cfgPreventEmojis: boolean

  constructor() {
    this.cfgRegex = null
    this.cfgReplace = null
    this.cfgEmpty = []
    this.cfgReplace = null
    this.cfgValidStrings = null
    this.cfgCaseInsensitive = false
    this.cfgPred = null
    this.cfgPreventEmojis = false
  }

  regex(regex: RegExp) {
    this.cfgRegex = regex
    return this
  }

  predicate(pred: (s: string) => boolean) {
    this.cfgPred = pred
    return this
  }

  valid(strings: string[]) {
    this.cfgValidStrings = strings
    return this
  }

  caseInsensitive() {
    this.cfgCaseInsensitive = true
    return this
  }

  replace(replaceRegex: RegExp, replacement: string) {
    this.cfgReplace = { replaceRegex, replacement }
    return this
  }

  empty(value: string) {
    this.cfgEmpty.push(value)
    return this
  }

  preventEmojis() {
    this.cfgPreventEmojis = true
    return this
  }

  validateStr(value: string): ValidatorResp<string> {
    let val = this.cfgCaseInsensitive ? value.toLowerCase() : value
    const empty = this.cfgCaseInsensitive ? this.cfgEmpty.map(v => v.toLowerCase()) : this.cfgEmpty

    if (empty.includes(val)) {
      return {}
    }

    const error = this.validatePred(value)
    if (error != null) {
      return { error }
    }

    if (this.cfgValidStrings != null) {
      const fn = this.caseInsensitive() ? (s: string) => s.toLowerCase() : (s: string) => s
      const validStr = this.cfgValidStrings.find(s => fn(s) === fn(val))
      if (validStr == null) {
        return { error: new Error(`value '${value}' missing from values list`) }
      }
      val = validStr
    }

    if (this.cfgRegex != null && !this.cfgRegex.exec(val)) {
      return { error: new Error(`value '${val}' failed regex validation`) }
    }

    if (this.cfgReplace) {
      const { replaceRegex, replacement } = this.cfgReplace
      val = val.replace(replaceRegex, replacement)
    }

    if (this.cfgPreventEmojis) {
      const containsEmoji = emojiRegex().test(value)
      if (containsEmoji) {
        return { error: new Error('Sorry, Lisa is not able to express herself with Emojis at this time') }
      }
    }

    return { value: val }
  }

  private validatePred(value: string): Error | null {
    if (this.cfgPred != null) {
      try {
        if (!this.cfgPred(value)) {
          return new Error(`value '${value}' does not match the predicate`)
        }
      } catch (error) {
        return error as Error
      }
    }
    return null
  }
}

class StringValidator extends BaseValidator implements Validator<string> {
  validate(value: string): ValidatorResp<string> {
    return this.validateStr(value)
  }
}

class NumberValidator extends BaseValidator implements Validator<number> {
  pred: ((arg: number) => boolean) | null

  constructor(pred?: (arg: number) => boolean) {
    super()
    this.pred = pred || null
  }

  validate(value: string): ValidatorResp<number> {
    const ret = super.validateStr(value)
    if (ret.error != null) {
      // value is error or empty, return
      return { error: ret.error }
    } else if (ret.value == null) {
      return { value: null }
    }

    // Believe it or not, the best way to check whether a string represents a valid number in Javascript is via isNaN...
    if (isNaN(value as any)) {
      return { error: new Error(`value '${value}' is not a number`) }
    }

    let valueNum
    try {
      valueNum = parseFloat(value)
    } catch (e) {
      return { error: new Error(`value '${value}' is not numeric`) }
    }

    const pred = this.pred
    if (pred && !pred(valueNum)) {
      return { error: new Error(`number ${valueNum} did not match the predicate`) }
    }

    return { value: valueNum }
  }
}

class IntegerValidator extends NumberValidator {
  validate(value: string): ValidatorResp<number> {
    const validNumber = super.validate(value)
    if (validNumber.error != null || validNumber.value == null) {
      // value is error or empty, return
      return validNumber
    }

    if (!Number.isInteger(validNumber.value)) {
      return { error: new Error(`number ${value} is not an integer`) }
    }

    return validNumber
  }
}

class JsonValidator extends StringValidator {
  pred: ((json: any) => boolean) | null

  constructor(pred?: (json: any) => boolean) {
    super()
    this.pred = pred || null
  }

  validate(value: string): ValidatorResp<string> {
    const validString = super.validate(value)
    if (validString.error != null || validString.value == null) {
      // value is error or empty, return
      return validString
    }

    try {
      const jsonValue = JSON.parse(value)
      const pred = this.pred
      if (pred && !pred(jsonValue)) {
        return {
          error: new Error(`json ${jsonValue} did not pass predicate`),
          value,
        }
      }

      return { value: JSON.stringify(jsonValue, null, 2) }
    } catch (error) {
      return {
        error: error as Error,
        value,
      }
    }
  }
}

function optionsValidator<T>(options: { [s: string]: T }): Validator<T> {
  return {
    validate: (value: string) => {
      if (!options.hasOwnProperty(value) && !options.hasOwnProperty(value.toUpperCase())) {
        return { error: new Error(`invalid option '${value}'`) }
      }
      return options.hasOwnProperty(value.toUpperCase())
        ? { value: options[value.toUpperCase()] }
        : { value: options[value] }
    },
  }
}

export function getTourTypeOptions() {
  const tourTypePs = powerSet([TourType.VIRTUAL, TourType.SELF_GUIDED, TourType.IN_PERSON])
  const permutations: any[] = []
  tourTypePs.forEach(s => permutations.push(...permute(s)))
  const tourTypeOptions: { [s: string]: TourType[] } = {}
  // Programmatically make a mapping of all tour type combinations in string format to a corresponding list
  permutations.forEach(p => {
    tourTypeOptions[p.join(',')] = p
  })
  return tourTypeOptions
}

export default {
  phone: () => new StringValidator().regex(regex.phone).replace(/[^+0-9]/g, ''),
  email: () => new StringValidator().regex(regex.email),
  isoDate: () => new StringValidator().regex(regex.isoDate).predicate(s => !isNaN(new Date(s).getTime())),
  username: () => new StringValidator().regex(regex.username),
  url: () => new StringValidator().regex(regex.url),
  alphanumeric: () => new StringValidator().regex(regex.alphanumeric),
  str: () => new StringValidator(),
  num: (pred?: (arg: number) => boolean) => new NumberValidator(pred),
  json: (pred?: (arg: any) => boolean) => new JsonValidator(pred),
  integer: (pred?: (arg: number) => boolean) => new IntegerValidator(pred),
  options: optionsValidator,
  tourType: () => optionsValidator(getTourTypeOptions()),
}
